import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SelectInput,
  ApiErrorText,
  SearchableSelect,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  IconTextItem,
  Loader,
  ServiceFeeDisplay,
  NumberInput,
} from '@/shared/components';
import {
  countriesFilter,
  formatReceivedAmount,
  getRequiredLabel,
  useModalStore,
  useSingleOptionAutoSelect,
} from '@/shared/lib';
import type { Country, DepositSelectOption } from '@/shared/types';
import { useModalScreen } from '@/features/deposit-bank-transfer/lib';
import type { UseFormReturn } from 'react-hook-form';
import type { BankTransferFormValues } from '../model/useDepositBankTransferFormLogic';

type CountryDropdownItem = Country & {
  value: string;
};
interface BankTransferFormProps {
  form: UseFormReturn<BankTransferFormValues>;
  watchedValues: BankTransferFormValues;
  handleFormSubmit: (data: BankTransferFormValues) => void;
  currencyOptions: DepositSelectOption[];
  minAmount: number;
  filteredCountries: Country[];
  bankOptions: {
    value: string;
    label: string;
  }[];
  commissionData:
    | {
        feeAmount: number;
        currencyCode: string;
        receiveAmount: number;
      }
    | undefined;
  shouldShowCommission: boolean;
  isSubmitInProgressWithoutErrors: boolean;
  formError: string | undefined;
  isLoading: boolean;
  firstError: string | undefined;
  isInvoiceDetailsOpen: boolean;
}

export function BankTransferForm({
  form,
  watchedValues,
  handleFormSubmit,
  currencyOptions,
  minAmount,
  filteredCountries,
  bankOptions,
  commissionData,
  shouldShowCommission,
  isSubmitInProgressWithoutErrors,
  formError,
  isLoading,
  firstError,
  isInvoiceDetailsOpen,
}: BankTransferFormProps) {
  const { t } = useTranslation();
  const { setMultipleState } = useModalStore();
  useModalScreen('createInvoice');

  if (!form) {
    return <div>Form is not available</div>;
  }

  const { getAllState } = useModalStore();
  const { screen } = getAllState() as { screen?: string };

  useEffect(() => {
    if (isInvoiceDetailsOpen) {
      setMultipleState({
        isLoading: false,
      });
      return;
    }

    const shouldShowLoading =
      screen === 'createInvoice' && isSubmitInProgressWithoutErrors;

    setMultipleState({
      isLoading: shouldShowLoading,
    });
  }, [
    setMultipleState,
    isSubmitInProgressWithoutErrors,
    screen,
    isInvoiceDetailsOpen,
  ]);

  if (firstError) {
    return <ApiErrorText error={firstError} />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[4rem]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form
          onSubmit={event => {
            event.preventDefault();
            form.handleSubmit(handleFormSubmit)(event);
          }}
          className="flex flex-col gap-[1.6rem]">
          <FormField
            control={form.control}
            name="bankCountryCode"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('depositMethods.bankTransfer.placeholders.bankCountry'),
                true
              );

              return (
                <FormItem>
                  <FormControl>
                    <SearchableSelect<CountryDropdownItem>
                      items={filteredCountries.map(country => ({
                        ...country,
                        value: country.code,
                      }))}
                      floatingLabel={label}
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={filteredCountries.length === 0}
                      searchFilter={(option, search) =>
                        countriesFilter(option, search)
                      }
                      renderItem={country => (
                        <div className="flex items-center justify-between w-full">
                          <IconTextItem
                            icon={country.link}
                            title={country.name}
                          />
                        </div>
                      )}
                      renderSelectedValue={value => {
                        const country = filteredCountries.find(
                          country => country.code === value
                        );

                        if (!country) return <></>;

                        return (
                          <div className="flex items-center justify-between w-full">
                            <IconTextItem
                              icon={country.link}
                              title={country.name}
                              variant="compact"
                            />
                          </div>
                        );
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('depositMethods.bankTransfer.placeholders.currency'),
                true
              );

              const { isDisabled: controlDisabled } = useSingleOptionAutoSelect(
                currencyOptions,
                field.value,
                field.onChange,
                !watchedValues.bankCountryCode || currencyOptions.length === 0
              );

              return (
                <FormItem>
                  <FormControl>
                    <SelectInput
                      floatingLabel={label}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={currencyOptions}
                      selectedKey="value"
                      labelKey="name"
                      disabled={controlDisabled}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="bankKey"
            render={({ field }) => {
              const label = getRequiredLabel(
                t('depositMethods.bankTransfer.placeholders.preferredBank'),
                true
              );

              const { isDisabled: controlDisabled } = useSingleOptionAutoSelect(
                bankOptions,
                field.value,
                field.onChange,
                !watchedValues.currencyCode || bankOptions.length === 0
              );

              return (
                <FormItem>
                  <FormControl>
                    <SelectInput
                      floatingLabel={label}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={bankOptions}
                      variant="simple"
                      disabled={controlDisabled}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => {
              let placeholder = watchedValues.currencyCode
                ? t('common.amountMinAmountPlaceholder', {
                    min: formatReceivedAmount(minAmount),
                    currency: watchedValues.currencyCode,
                  })
                : t('common.amountPlaceholder');

              placeholder = getRequiredLabel(placeholder, true);

              return (
                <FormItem>
                  <FormControl>
                    <NumberInput
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      type="text"
                      inputMode="decimal"
                      label={placeholder}
                      isCustomElement
                      className="h-[6rem] rounded-3xl p3"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {shouldShowCommission && commissionData && (
            <ServiceFeeDisplay
              className="mt-[-1.2rem]"
              commissionData={commissionData}
            />
          )}

          {formError ? <ApiErrorText error={formError} /> : null}
        </form>
      </Form>
    </div>
  );
}
